import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { JsonObject } from '@se/cp.common';
import { ExaminationWorklistDTO, ExecutionWorklistSearchWorklist } from '@se/models';
import { ColDef, RowSelectionOptions } from 'ag-grid-community';
import { Observable, Subscription, catchError, filter, finalize, map, of, switchMap, take } from 'rxjs';
import { z } from 'zod';
import { RootState } from '../../root-store/root.state';
import { seSidePatientHistoryComponentNoResult } from '../../se-side/se-side-components/se-side-patient-history/redux/se-side-patient-history.actions';
import { seToolbarDisableAllButtons } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.actions';
import { selectSeToolbarTimeLineOpen } from '../../se-toolbar/se-buttons-toolbar/redux/se-buttons-toolbar.selectors';
import { SearchPageDiff } from '../../search-page/redux/search-page.state';
import { ModalDialogNotificationService, NotificationService } from '../../services/notification.service';
import { validateExaminationWorklistDTO } from '../../validation';
import {
    seSearchIfFiltersEq,
    seSearchSafeExit,
    seSearchSetItems,
    seSearchToggleRightArea,
    seSearchToggleTimeline,
    seSearchTypeChange,
} from '../redux/search-page.actions';
import {
    selectSeActualSearchType,
    selectSeSearchItems,
    selectSeToolbarRightSliderChecked,
} from '../redux/search-page.selectors';
import { SearchPageConfigurationService } from '../search-page-configuration.service';
import { SearchFormType } from '../search-page-type.enum';
import { SearchPageService } from '../search-page.service';
import { SearchParams, SearchTypeInfo } from '../search-param.model';

@Component({
    selector: 'se-ap-search-container',
    templateUrl: './ap-search-container.component.html',
    styleUrls: ['./ap-search-container.component.scss'],
    standalone: false,
})
export class ApSearchContainerComponent implements OnInit, OnDestroy {
    get disabledShowHideIdList(): string[] {
        // Lista degli id relativi alle colonne che non devono essere inseriti nel menu' Show\Hide
        return [
            'FeaAuthHead',
            'FeaExamConsentHead',
            'Doses',
            'TicketOkHead',
            'SendToWorklist',
            'AttachedDocuments',
            'ClinicalAlert',
        ];
    }

    searchResult$?: Observable<any[]>;
    disabledShowHideColList: ColDef[] = [];
    idPropertyName: string = 'id'; // DGS_AP: cablato ma da recuperare dalla DUMMY?
    disableSearch: boolean = false;
    actualSearchType?: SearchTypeInfo;
    hideFilters: boolean = false;

    _actionSubscription?: Subscription;
    _hideFiltersSubscription?: Subscription;
    _hideRightAreaSubscription?: Subscription;
    _hideTimelineSubscription?: Subscription;
    _searchTypeSubscription?: Subscription;

    _formType!: SearchFormType;
    _isBusy: boolean = false;

    @Input()
    set formType(value: SearchFormType) {
        this._formType = value;
        this.searchResult$ = this.store.pipe(select(selectSeSearchItems(value)));
    }

    @Input() canItemBeSelected?: (item: JsonObject, actualSelectedItems: JsonObject[]) => boolean;

    @Input()
    getButtonContentForSelection?: (rowData: JsonObject) => any;

    @Input()
    rowSelection: RowSelectionOptions = {
        mode: 'singleRow',
        checkboxes: false, // elimina le chackbox che compaiono di default su ogni riga
        enableClickSelection: true, // abilitaa la selezione con il click - obbligatorio se si eliminano le checkbox
        enableSelectionWithoutKeys: false, // se true, la multi selezione si può fare solo con il click - se false, è necessario premere ctrl
    };

    @Input()
    canPatientHistoryOpenForSelection: Function = (actualSelectedItems: JsonObject[]) => {
        return actualSelectedItems && actualSelectedItems.length == 1; // By default si apre solo se c'è una sola selezione
    };

    @Input()
    searchOnInit: boolean = true;

    @Input()
    getItemDiff?: (item: any) => SearchPageDiff;

    @Input()
    set isBusy(value: boolean) {
        let delay = value ? 0 : 1000;
        setTimeout(() => {
            this._isBusy = value;
            if (this._isBusy) {
                this.store.dispatch(seToolbarDisableAllButtons());
            }
        }, delay);
    }

    get searchParams(): SearchParams {
        return this.searchPageService.getSearchParams(this.formType);
    }

    set searchParams(params: SearchParams) {
        this.searchPageService.setSearchParams(this.formType, params);
    }

    get searchTypes(): SearchTypeInfo[] {
        const types = this.searchPageConfigurationService.getSearchTypes(this.formType);
        if (!types || types.length < 1) {
            return [];
        }
        return types;
    }

    get isMultiSearchEnabled() {
        return !!this.searchTypes && this.searchTypes.length > 0;
    }

    get isBusy(): boolean {
        return this._isBusy;
    }

    get formType() {
        return this._formType;
    }

    get runValidation(): boolean {
        //@ts-ignore
        return Boolean(window['activateValidation']);
    }

    constructor(
        private actions: Actions,
        private searchPageService: SearchPageService,
        private searchPageConfigurationService: SearchPageConfigurationService,
        private notificationService: NotificationService,
        private modalDialogNotificationService: ModalDialogNotificationService,
        private translate: TranslateService,
        private store: Store<RootState>,
    ) {}

    ngOnDestroy() {
        this._actionSubscription?.unsubscribe();
        if (this._hideFiltersSubscription) this._hideFiltersSubscription.unsubscribe();
        if (this._hideRightAreaSubscription) this._hideRightAreaSubscription.unsubscribe();
        if (this._hideTimelineSubscription) this._hideTimelineSubscription.unsubscribe();
        if (this._searchTypeSubscription) this._searchTypeSubscription.unsubscribe();

        this.store.dispatch(seSearchSafeExit({ formType: this.formType }));
    }

    ngOnInit() {
        this.store.dispatch(seSearchTypeChange({ formType: this.formType }));

        // Prima leggo lo stato salvato degli slider e poi attacco i listener
        this.listenForStoreEvents();
        this.searchParams.clearSearchParamsOnBack();
    }

    handleSearchRequired() {
        this.getResults().subscribe((newItems) => {
            this.store.dispatch(seSearchSetItems({ formType: this.formType, items: newItems }));

            // Comunico al componente side che la ricerca non ha prodotto risultati
            if (!newItems || newItems.length <= 0) {
                this.seSideFailedSearchOps('NoResults');
            }
        });
    }

    private listenForStoreEvents() {
        this._actionSubscription = this.actions
            .pipe(
                ofType(seSearchIfFiltersEq),
                filter((action) => {
                    if (action.formType !== this.formType) {
                        return false;
                    }
                    if ('filters' in action) {
                        return this.searchParamsMatch(action.filters);
                    }
                    return true;
                }),
            )
            .subscribe((_) => {
                this.handleSearchRequired();
            });

        /* Questa non c'è piu'semmai la dobbiamo legare al pannello dei filtri sopra
        this._hideFiltersSubscription = this.store
            .select('buttonsToolbar', 'leftSlider', 'checked')
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleLeftArea({ formType: this.formType, hide: !checked })); // Notifico lo stato
                this.hideFilters = !checked; // nascondo la toolbar
            });
        */

        this._hideRightAreaSubscription = this.store
            .select(selectSeToolbarRightSliderChecked(this._formType))
            .subscribe((checked) => {
                this.store.dispatch(seSearchToggleRightArea({ formType: this.formType, hide: checked })); // Notifico lo stato
            });

        this._hideTimelineSubscription = this.store.select(selectSeToolbarTimeLineOpen).subscribe((open) => {
            this.store.dispatch(seSearchToggleTimeline({ formType: this.formType, hide: open })); // Notifico lo stato
        });

        this._searchTypeSubscription = this.store
            .select(selectSeActualSearchType(this._formType))
            .subscribe((st) => (this.actualSearchType = st));
    }

    private searchParamsMatch(filters: Record<string, any>) {
        const searchParams = this.searchParams;

        for (const filterKey in filters) {
            if (
                Object.hasOwnProperty.call(filters, filterKey) &&
                !searchParams.matches(filterKey, filters[filterKey])
            ) {
                return false;
            }
        }
        return true;
    }

    private getResults() {
        this.isBusy = true;

        return this.store.pipe(
            select('searchPage', this.formType, 'actualSearchType'),
            take(1),
            switchMap((searchType) => {
                const searchInfo = {
                    ...this.searchParams.toJSON(),
                    searchType: searchType ? searchType.id : null,
                } as unknown as ExecutionWorklistSearchWorklist;

                if (this.runValidation) {
                    return this.searchPageService.getResult(this.formType, searchInfo).pipe(
                        map((apExaminations) => {
                            return apExaminations.map((apExamination: any, index: number) => {
                                try {
                                    return validateExaminationWorklistDTO(apExamination);
                                } catch (error) {
                                    if (error instanceof z.ZodError) {
                                        console.error(
                                            `Validation failed for examination at index ${index}:`,
                                            error.errors,
                                        );
                                        throw new Error(`Invalid examination data at index ${index}: ${error.message}`);
                                    }
                                    throw error;
                                }
                            });
                        }),
                    );
                }

                return this.searchPageService.getResult(this.formType, searchInfo) as Observable<
                    ExaminationWorklistDTO[]
                >;
            }),
            catchError((e) => {
                // TooMuch??
                if (e?.error?.estensaFaultDetails?.typeName == 'TooMuchResultFault') {
                    try {
                        let messageKey = 'Exception_' + e.error.estensaFaultDetails.typeName;

                        let params: any = {};
                        let position = 0;
                        for (let param in e.error.estensaFaultDetails.estensaFault) {
                            if (param === 'errorId' || param === 'message' || param === 'completeMessage') {
                                continue;
                            }
                            params['p' + position] = e.error.estensaFaultDetails.estensaFault[param];
                            position++;
                        }

                        this.modalDialogNotificationService.warning(this.translate.instant(messageKey, params));
                    } catch {
                        this.notificationService.error(
                            e.message ? e.message : '',
                            this.translate.instant('ErrorLoadingData'),
                        );
                    }
                } else
                    this.notificationService.error(
                        e.message ? e.message : '',
                        this.translate.instant('ErrorLoadingData'),
                    );

                // SeSide
                this.seSideFailedSearchOps('ErrorLoadingData');

                return of([]);
            }),
            finalize(() => {
                this.isBusy = false;
            }),
        );
    }

    private seSideFailedSearchOps(message: string) {
        // Patient History
        this.store.dispatch(
            seSidePatientHistoryComponentNoResult({
                formType: this.formType,
                message: this.translate.instant(message),
            }),
        );
    }

    fnCanItemBeSelectedHandler = (itemToSelect: JsonObject, actualSelectedItems: JsonObject[]) => {
        if (this.canItemBeSelected) {
            return this.canItemBeSelected(itemToSelect, actualSelectedItems);
        }

        return true;
    };

    fnCanPatientHistoryOpenForSelectionHandler = (actualSelectedItems: JsonObject[]) => {
        if (this.canPatientHistoryOpenForSelection) {
            return this.canPatientHistoryOpenForSelection(actualSelectedItems);
        }
        return false;
    };

    fnGetButtonContentForSelectionHandler = (rowData: JsonObject) => {
        if (this.getButtonContentForSelection) {
            return this.getButtonContentForSelection(rowData);
        }

        return null;
    };

    handlePrimaryChange = ({ colId, primary }: { colId: string; primary: boolean }) => {
        this.searchPageConfigurationService.setColDefPrimary(this.formType, colId, primary);
    };

    handleVisibilityChange = ({ colId, isVisible }: { colId: string; isVisible: boolean }) => {
        this.searchPageConfigurationService.setColDefVisibility(this.formType, colId, isVisible);
    };

    onParamsChanged(params: SearchParams) {
        this.searchParams = params;
    }
}
